import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { CaseStudyTOC } from "@/components/caseStudy/CaseStudyTOC";
import { getGuide, GUIDES, type Guide } from "@/content/guides";
import { pageMeta, ldScripts, breadcrumbLd, articleLd, faqLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";


export const Route = createFileRoute("/guides/$slug")({
  loader: async ({ params }) => {
    const { getGuide } = await import("@/content/guides");
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: `Guide not found · ${siteConfig.name}` }, { name: "robots", content: "noindex" }] };
    }
    const { guide } = loaderData;
    const title = `${guide.title} · ${siteConfig.name}`;
    const path = `/guides/${params.slug}/`;
    const base = pageMeta({
      path,
      title,
      description: guide.dek,
      type: "article",
      keywords: [
        guide.title,
        "SEO guide",
        `${siteConfig.contact.address.city} SEO`,
        "AI search",
        "GEO",
        siteConfig.name,
      ],
    });
    return {
      ...base,
      meta: [
        ...base.meta,
        { property: "article:modified_time", content: guide.updatedISO },
        { name: "author", content: siteConfig.founder.name },
      ],
      scripts: ldScripts(
        articleLd({ headline: guide.title, description: guide.dek, path, datePublished: guide.updatedISO }),
        // The HQ LocalBusiness node is emitted once sitewide from __root; re-emitting
        // it here would duplicate the #localbusiness @id and confuse the entity graph.
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources/" },
          { name: "Guides", path: "/guides/" },
          { name: guide.title, path },
        ]),
        ...(guide.faqs?.length ? [faqLd(guide.faqs)] : []),
      ),
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-32">
      <h1 className="font-display text-4xl">Guide not found</h1>
      <Link to="/guides/" className="mt-4 inline-block text-primary">← Back to guides</Link>
    </div>
  ),
  component: GuidePage,
});

function GuidePage() {
  const { guide } = Route.useLoaderData() as { guide: Guide };
  const toc = guide.chapters.map((c, i) => ({
    id: `c-${i}`,
    label: `${String(i + 1).padStart(2, "0")} · ${c.h}`,
  }));

  const related = (guide.related ?? [])
    .map(getGuide)
    .filter(Boolean) as NonNullable<ReturnType<typeof getGuide>>[];
  const others = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 3);

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <div className="container-page pt-8 text-xs uppercase tracking-widest text-muted-foreground">
          <Link to="/resources/" className="hover:text-foreground">Resources</Link>
          <span className="mx-2">/</span>
          <Link to="/guides/" className="hover:text-foreground">Guides</Link>
        </div>

        <div className="container-page pt-2">
          <div className="max-w-4xl rounded-2xl border border-border/70 bg-card/40 p-4 md:p-5 text-sm text-foreground/85 leading-relaxed">
            <span className="font-medium text-foreground">Written from {siteConfig.contact.address.city}, {siteConfig.contact.address.state}</span>{" "}
            — {siteConfig.founder.name}, founder of {siteConfig.name}, distilling live SEO and AI-search
            engagements with {siteConfig.contact.address.city} and {siteConfig.contact.address.state} businesses into an operating manual.

          </div>
        </div>

        <section className="container-page pt-8 pb-14 max-w-4xl">
          <EyebrowLabel>Pillar guide</EyebrowLabel>
          <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {guide.title}
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">{guide.dek}</p>
          <div className="mt-8 grid gap-6 md:grid-cols-3 text-sm">
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Audience</div>
              <div className="mt-2 text-foreground/90 leading-relaxed">{guide.audience}</div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Outcome</div>
              <div className="mt-2 text-foreground/90 leading-relaxed">{guide.outcome}</div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Meta</div>
              <div className="mt-2 text-foreground/90">{guide.readingMinutes} min read</div>
              <div className="text-muted-foreground text-xs mt-1">Updated {new Date(guide.updatedISO).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
            </div>
          </div>
        </section>

        <section className="container-page pb-24 grid gap-12 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <CaseStudyTOC items={toc} />
            </div>
          </aside>

          <article className="max-w-3xl">
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
              <div className="text-xs uppercase tracking-widest text-primary">TL;DR</div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/90">
                {guide.tldr.map((t) => (
                  <li key={t} className="flex gap-2"><span className="text-primary">·</span>{t}</li>
                ))}
              </ul>
            </div>

            {guide.chapters.map((c, i) => (
              <section key={i} id={`c-${i}`} className="mt-16 scroll-mt-24">
                <div className="text-xs uppercase tracking-widest text-primary">Chapter {String(i + 1).padStart(2, "0")}</div>
                <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                  {c.h}
                </h2>
                {c.p.map((para, j) => (
                  <p key={j} className="mt-5 text-[1.05rem] leading-relaxed text-foreground/85">
                    {para}
                  </p>
                ))}
                {c.bullets && c.bullets.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {c.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 text-[1.02rem] leading-relaxed text-foreground/85">
                        <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {c.callout && (
                  <div className="mt-6 rounded-xl border-l-4 border-primary bg-primary/5 p-5 text-foreground/90 italic">
                    {c.callout}
                  </div>
                )}
              </section>
            ))}

            {guide.faqs && guide.faqs.length > 0 && (
              <section id="faqs" className="mt-16 scroll-mt-24">
                <div className="text-xs uppercase tracking-widest text-primary">FAQs</div>
                <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                  Questions we get about this guide
                </h2>
                <dl className="mt-8 space-y-6">
                  {guide.faqs.map((f, i) => (
                    <div key={i} className="rounded-2xl border border-border p-6">
                      <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                      <dd className="mt-3 text-foreground/85 leading-relaxed">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}


            <div className="mt-16 rounded-3xl border border-border p-8 md:p-10 bg-gradient-to-br from-primary/10 to-transparent">
              <EyebrowLabel>Put it into practice</EyebrowLabel>
              <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">
                Want us to run this plan for you?
              </h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                This guide is how we think. A 30-minute conversation is how we translate it into
                a program you can budget against.
              </p>
              <div className="mt-6"><ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="md">Start a project <span aria-hidden>→</span></ButtonLink></div>
            </div>
          </article>
        </section>

        <section className="container-page pb-32">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-6">Related guides</div>
          <div className="grid gap-6 md:grid-cols-3">
            {(related.length ? related : others).slice(0, 3).map((r) => (
              <Link
                key={r.slug}
                to="/guides/$slug/"
                params={{ slug: r.slug }}
                className="group rounded-2xl border border-border p-6 bg-card/30 hover:bg-card/50 hover:border-primary/40 transition-all"
              >
                <EyebrowLabel>Guide</EyebrowLabel>
                <h4 className="mt-3 font-display text-xl leading-tight tracking-tight">{r.title}</h4>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.dek}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
